import type { ViewField } from '@lighthouse/core'
import { type DataSourceAbstract, type FilterCommonCondition, type QuickFilterData, VariableType } from '@lighthouse/core'
import { camelToKebab, useDebounce } from '@lighthouse/tools'
import isDeepEqual from 'fast-deep-equal'
import { find } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'
import { useUpdateEffect } from 'react-use'
import styled, { css } from 'styled-components'

import type { FilterInteractionStyles, FilterStyles } from '../../hooks'
import { useFilterInteractionStyles, useFilterStyles } from '../../hooks'
import { getVariableFieldId } from '../../utils'
import QuickFilterActor from './QuickFilterActor'

const SCxStyleContent = styled.div<{
    filterStyle?: FilterStyles
    filterInteractionStyle?: FilterInteractionStyles
}>`
    display: flex;
    justify-content: flex-start;
    gap: 8px;
    /* filter */
    ${({ filterStyle }) =>
        filterStyle?.filter &&
        css`
            & [data-filter-input] {
                ${Object.entries(filterStyle.filter)
                    .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
                    .join('\n')}
            }
        `}

    ${({ filterInteractionStyle }) =>
        filterInteractionStyle?.filter?.hoverStyle &&
        css`
            & [data-filter-input][data-filter-active='false'][data-filter-disabled='false']:hover {
                ${Object.entries(filterInteractionStyle.filter.hoverStyle)
                    .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
                    .join('\n')}
            }
        `}

        ${({ filterInteractionStyle }) =>
        filterInteractionStyle?.filter?.activeStyle &&
        css`
            & [data-filter-input][data-filter-active='true'] {
                ${Object.entries(filterInteractionStyle.filter.activeStyle)
                    .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
                    .join('\n')}
            }
        `}

        ${({ filterInteractionStyle }) =>
        filterInteractionStyle?.filter?.disabledStyle &&
        css`
            & [data-filter-input][data-filter-disabled='true'] {
                ${Object.entries(filterInteractionStyle.filter.disabledStyle)
                    .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
                    .join('\n')}
            }
        `}

    /* icon */

    ${({ filterStyle }) =>
        filterStyle?.icon &&
        css`
            & [data-filter-input] [data-filter-icon] {
                ${Object.entries(filterStyle.icon)
                    .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
                    .join('\n')}
            }
        `}


           ${({ filterInteractionStyle }) =>
        filterInteractionStyle?.icon?.hoverStyle &&
        css`
            & [data-filter-active='false'][data-filter-disabled='false'] [data-filter-icon]:hover {
                ${Object.entries(filterInteractionStyle.icon.hoverStyle)
                    .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
                    .join('\n')}
            }
        `}



           ${({ filterInteractionStyle }) =>
        filterInteractionStyle?.icon?.activeStyle &&
        css`
            & [data-filter-active='true'] [data-filter-icon] {
                ${Object.entries(filterInteractionStyle.icon.activeStyle)
                    .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
                    .join('\n')}
            }
        `}


           ${({ filterInteractionStyle }) =>
        filterInteractionStyle?.icon?.disabledStyle &&
        css`
            & [data-filter-disabled='true'] [data-filter-icon] {
                ${Object.entries(filterInteractionStyle.icon.disabledStyle)
                    .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
                    .join('\n')}
            }
        `}
`

export interface FilterActorGroupProps {
    mode?: 'normal' | 'tile'
    enable?: boolean
    dataSource: DataSourceAbstract
    columns: ViewField[]
    quickFilter: QuickFilterData
    disablePortal?: boolean
    isMobile?: boolean
    onFilter?: (data: FilterCommonCondition[]) => void
}

export const FilterActor: React.FC<FilterActorGroupProps> = ({
    mode = 'normal',
    enable = true,
    dataSource,
    columns,
    quickFilter,
    disablePortal,
    isMobile,
    onFilter
}) => {
    const isTileQuickFilter = quickFilter?.mode === 'tile'
    const { schema } = dataSource
    const [filter, setFilter] = useState(quickFilter?.rules?.filter(item => item.idVariable?.type === VariableType.FIELD_ID) || [])
    const filteredQuickFilterRules = useMemo(
        () =>
            filter?.filter(item => {
                if (!item.idVariable) {
                    return false
                }
                const fieldId = getVariableFieldId(item.idVariable)
                return fieldId && schema[fieldId]
            }),
        [filter, schema]
    )

    const filterStyle = useFilterStyles()
    const filterInteractionStyle = useFilterInteractionStyles()

    useUpdateEffect(() => {
        if (quickFilter.mode === 'disable') {
            return
        }
        if (
            !isDeepEqual(
                quickFilter.rules.map(item => getVariableFieldId(item.idVariable)),
                filter.map(item => getVariableFieldId(item.idVariable))
            )
        ) {
            setFilter(quickFilter?.rules || [])
            onFilter?.(quickFilter?.rules || [])
        }
    }, [quickFilter])

    const debouncedFilter = useDebounce(filter, 500)

    useUpdateEffect(() => {
        if (!isDeepEqual(debouncedFilter, quickFilter?.rules)) {
            onFilter?.(debouncedFilter)
        }
    }, [debouncedFilter])

    const handleFilterItem = useCallback(
        (data: FilterCommonCondition) => {
            const newFilterItems = filteredQuickFilterRules.map(item => {
                if (getVariableFieldId(item?.idVariable) === getVariableFieldId(data?.idVariable)) {
                    return data
                }
                return item
            })
            setFilter?.(newFilterItems)
        },
        [filteredQuickFilterRules]
    )

    if (!enable || !filter || filteredQuickFilterRules.length === 0) {
        return null
    }

    return (
        <SCxStyleContent
            style={{ flexDirection: isTileQuickFilter ? 'column' : 'row' }}
            filterStyle={filterStyle}
            filterInteractionStyle={filterInteractionStyle}
        >
            {filteredQuickFilterRules.map(filterItem => {
                const { idVariable } = filterItem
                const fieldId = getVariableFieldId(idVariable)
                if (!fieldId) {
                    return null
                }
                const viewField = find(item => item.fieldId === fieldId, columns)
                if (viewField?.type === 'formula' && viewField?.innerType === 'NULL') {
                    return null
                }
                return (
                    viewField && (
                        <QuickFilterActor
                            mode={mode}
                            columns={columns}
                            dataSource={dataSource}
                            disablePortal={disablePortal}
                            key={fieldId}
                            viewField={viewField}
                            isMobile={isMobile}
                            filterItem={filterItem}
                            onFilter={handleFilterItem}
                        />
                    )
                )
            })}
        </SCxStyleContent>
    )
}
